import { useEffect, useState, useRef, useCallback, useId } from 'react';
import { useSnackbar } from 'notistack';
import { FormControl, Box, InputLabel } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import { GeminiEditor } from 'components/GeminiEditor/GeminiEditor';
import { useSelector, useDispatch } from 'react-redux';
import { ErrorMessage, Field, Form, Formik, useFormikContext } from 'formik';

import { ContentTypes } from 'helpers';
import { makeFormStyles } from 'forms/styles';
import DirtyProcedure from '../DirtyProcedure';
import { makeContentNodeFormStyles } from './styles';
import { FormTextField } from 'components/TextField';
import { useTinyMCEInit } from 'hooks/useTinyMCEInit';
import SaveNodeDataDialog from '../SaveNodeDataDialog';
import { TinyMCEEditor } from 'components/TinyMCEEditor';
import SelectTargetNodeForm from './SelectTargetNodeForm';
import { setProcedureDirty } from 'features/procedures/proceduresSlice';
import { AssociatedView } from './SelectTargetNodeForm/components/AssociatedView';
import { SnackbarDismiss } from 'GeminiViewerComponent/components/SnackbarDismiss';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { CommonNodeFormFields, NodeCategoryField } from '../CommonNodeFormFields';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import { loadZone } from 'shared/loadZoneSlice';
import { closeAllPanel } from 'features/common/formSlice';
import { assetService } from 'features/services';
import { setOrbitImages } from 'GeminiViewerComponent/features/asset/assetSlice';
import { accountsSlice } from 'app/store';
import {
    ContentNodeFormAnswerOptionFields,
    ContentNodeFormAnswerOptions,
    ContentNodeFormFailureDetails,
    ContentNodeFormFields,
    ContentNodeFormPreset,
    ContentNodeFormPreview,
    ContentNodeFormSettings,
} from './components/ContentNodeFormFields';
import { createContent } from 'features/content/contentSlice';

const ContentNodeForm = ({
    assetId,
    nodes,
    node,
    onCancel,
    onSubmit,
    isValidVariableName,
    checkUniqueVariableName,
    openSaveDataDialog,
    setOpenSaveDataDialog,
    showTwoPanelMode,
    procedureCategories,
    isNewNode = false,
}) => {
    const { selectActiveUser } = accountsSlice;
    const theme = useSelector(selectDefaultTheme);
    const activeUser = useSelector(selectActiveUser);
    const nodeFormStyles = makeContentNodeFormStyles(theme);
    const commonFormStyles = makeFormStyles(theme);
    const dispatch = useDispatch();
    const init = useTinyMCEInit();
    const { enqueueSnackbar } = useSnackbar();
    const editorInstanceId = useId().replace(/:/g, '').replace(/^/, 'content-editor-');

    const deriveAnswerGroup = (n) => {
        const group = n?.answer_group;
        if (group === 'hotspot' || group === 'image' || group === 'select' || group === 'button') {
            return group;
        }
        // answer_group not explicitly set — infer from option arrays
        if (Array.isArray(n?.hotspot_options) && n.hotspot_options.length > 0) return 'hotspot';
        if (Array.isArray(n?.image_options) && n.image_options.length > 0) return 'image';
        if (Array.isArray(n?.select_options) && n.select_options.length > 0) return 'select';
        if (Array.isArray(n?.option_buttons) && n.option_buttons.length > 0) return 'button';
        return group; // undefined/null — no answer type selected yet
    };

    const buildInitialValues = (n, cats) => ({
        close_associated_view: false,
        variable: '',
        ...n,
        procedureCategories: cats,
        exclude_from_navigation: n?.exclude_from_navigation ?? false,
        answer_group: deriveAnswerGroup(n),
    });

    const [prevNodeId, setPrevNodeId] = useState(node.id);
    const [prevAnswerGroup, setPrevAnswerGroup] = useState(() => deriveAnswerGroup(node));
    const [formValues, setFormValues] = useState(() => buildInitialValues(node, procedureCategories));

    const currentAnswerGroup = deriveAnswerGroup(node);
    if (node.id !== prevNodeId || currentAnswerGroup !== prevAnswerGroup) {
        setPrevNodeId(node.id);
        setPrevAnswerGroup(currentAnswerGroup);
        setFormValues(buildInitialValues(node, procedureCategories));
    }

    const [selectTargetNodeForEditor, setSelectTargetNodeForEditor] = useState(null);

    const [loadedTargetNode, setLoadedTargetNode] = useState(null);
    const [pendingFilesToUpload, setPendingFilesToUpload] = useState([]);
    const [openEditFieldForm, setOpenEditFieldForm] = useState(false);
    const editorRef = useRef(null);
    const titleInputRef = useRef(null);
    const procedureRef = useRef(null);
    const [completionNode, setCompletionNode] = useState(null);
    const [selectedField, setSelectedField] = useState(null);
    const [showInitialViewDialog, setShowInitialViewDialog] = useState(false);
    const [initialViewValues, setInitialViewValues] = useState({ yaw: '', pitch: '', zoom: '' });
    const [showGeminiEditor, setShowGeminiEditor] = useState(false);
    const [geminiTarget, setGeminiTarget] = useState(null); // { asset_id, zone_id, item_id }
    const setFieldValueRef = useRef(null);

    const focusTitleField = () => {
        const titleField = titleInputRef.current;
        if (titleField) {
            titleField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            titleField.focus();
            titleField.select?.();
        }
    };

    const TitleFocusHandler = () => {
        const { errors, submitCount, setFieldTouched } = useFormikContext();

        useEffect(() => {
            if (submitCount > 0 && errors?.title) {
                setFieldTouched('title', true, false);
                focusTitleField();
            }
        }, [errors?.title, setFieldTouched, submitCount]);

        return null;
    };

    useEffect(() => {
        setFormValues(buildInitialValues(node, procedureCategories));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node.id, node?.node_category, node?.isNew, node?.answer_group]);

    useEffect(() => {
        setFormValues((prev) => ({
            ...prev,
            procedureCategories,
        }));
    }, [procedureCategories]);

    useEffect(() => {
        return () => {
            setFormValues({});
            dispatch(setProcedureDirty(false));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSelectedTargetNode = (
        { link_text, is_button, asset_id, zone_id, item_id, link_id, node_id },
        selectTargetNodeForEditor
    ) => {
        if (node_id && link_text) {
            const strToUseStart = (is_button ? `<button data-target-node="true">` : ``) + `<a `;
            const strToUseEnd = `</a>` + (is_button ? `</button>` : ``);
            let targetNodeLink = `${strToUseStart} data-target-node="true" href="?node_id=${node_id?.id}`;
            if (asset_id) {
                targetNodeLink += `&asset_id=${asset_id}`;
                if (zone_id) {
                    targetNodeLink += `&zone_id=${zone_id}`;
                    if (item_id) {
                        targetNodeLink += `&item_id=${item_id}`;
                        if (link_id) {
                            targetNodeLink += `&link_id=${link_id}`;
                        }
                    }
                }
            }
            targetNodeLink += `" target="_self">` + link_text + strToUseEnd;
            if (loadedTargetNode?.actionType === 'edit') {
                let editor = selectTargetNodeForEditor;
                updateElement(editor, loadedTargetNode.targetEl, targetNodeLink);
            } else {
                selectTargetNodeForEditor.insertContent(targetNodeLink + '&nbsp;&nbsp;');
            }
        }
        setSelectTargetNodeForEditor(null);
    };

    const updateElement = (editor, nodeLinkElm, newContent) => {
        editor.selection.select(nodeLinkElm);
        editor.selection.setContent(newContent);
    };

    const getAnchorElement = (editor, selectedElm) => {
        selectedElm = selectedElm || editor.selection.getNode();
        const parent = editor.dom.getParent(selectedElm, 'a[href]');
        return parent;
    };

    const getButtonElement = (editor, selectedElm) => {
        selectedElm = selectedElm || editor.selection.getNode();
        const parent = editor.dom.getParent(selectedElm, 'button');
        return parent;
    };

    const handleTargetNodeAction = (editor) => {
        const anchor = getAnchorElement(editor, editor.selection.getNode());

        if (!anchor) {
            setLoadedTargetNode(null);
            setSelectTargetNodeForEditor(editor);
            return;
        }

        const button = getButtonElement(editor, editor.selection.getNode());
        const targetEl = button || anchor;

        if (targetEl?.getAttribute('data-target-node')) {
            setLoadedTargetNode({
                targetEl: targetEl,
                href: anchor.getAttribute('href'),
                isButton: button !== null,
                link_text: anchor.innerText,
                actionType: 'edit',
            });
            setSelectTargetNodeForEditor(editor);
        } else {
            enqueueSnackbar(`The selected content is not a target node.`, {
                action: (key) => <SnackbarDismiss key={key} />,
                variant: 'error',
            });
        }
    };

    const getFileExtension = (file) => {
        const fileName = file?.name || file?.path;
        const re = /(?:\.([^.]+))?$/;
        const extension = re.exec(fileName)[1];
        const ext = extension?.replace(/^.{1}/g, extension?.[0]);
        return {
            fileName: fileName?.substring(0, fileName?.lastIndexOf('.')),
            fileExtension: ContentTypes[ext.charAt(0).toUpperCase() + ext.substring(1).toLowerCase()]?.id,
        };
    };

    const getBase64 = (url) => {
        return new Promise((resolve) => {
            var xhr = new XMLHttpRequest();
            xhr.onload = function () {
                var reader = new FileReader();
                reader.onloadend = () => {
                    resolve(reader.result);
                };
                reader.readAsDataURL(xhr.response);
            };
            xhr.open('GET', url);
            xhr.responseType = 'blob';
            xhr.send();
        });
    };

    const selectCompletionNode = (value, setFieldValue) => {
        if (
            nodes.find((node) => node?.completion === true) &&
            nodes.find((node) => node?.completion === true).id !== node.id
        ) {
            setCompletionNode(nodes.find((node) => node?.completion === true));
        } else {
            setCompletionNode(null);
            setFieldValue('completion', value);
        }
    };

    const file_picker_callback_function = (cb, value, meta) => {
        var input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute(
            'accept',
            meta?.filetype === 'media'
                ? ['video/mp4', 'audio/mp3']
                : ['image/jpg', 'image/jpeg', 'image/png', 'image/svg+xml']
        );

        /*
            Note: In modern browsers input[type="file"] is functional without
            even adding it to the DOM, but that might not be the case in some older
            or quirky browsers like IE, so you might want to add it to the DOM
            just in case, and visually hide it. And do not forget do remove it
            once you do not need it anymore.
        */

        input.onchange = function () {
            var file = this.files[0];
            var extension = file.name.split('.').pop().toLowerCase();
            var validExtension = meta?.filetype === 'media' ? ['mp4', 'mp3'] : ['jpg', 'jpeg', 'png', 'svg'];
            if (validExtension.indexOf(extension) > -1) {
                var reader = new FileReader();
                reader.onload = async function () {
                    /*
                        Note: Now we need to register the blob in TinyMCEs image blob
                        registry. In the next release this part hopefully won't be
                        necessary, as we are looking to handle it internally.
                    */
                    var id = 'blobid' + new Date().getTime();
                    var blobCache = editorRef.current.editorUpload.blobCache;
                    var base64 = reader.result.split(',')[1];
                    var blobInfo = blobCache.create(id, file, base64);
                    const base64Data = await getBase64(blobInfo.blobUri());
                    setPendingFilesToUpload((prevState) => {
                        const state = [...prevState];
                        state.push({
                            oldUrl: base64Data,
                            fileToUpload: file,
                        });
                        return state;
                    });
                    blobCache.add(blobInfo);
                    /* call the callback and populate the Title field with the file name */
                    cb(blobInfo.blobUri(), {
                        title: file.name,
                        height: extension === 'mp3' ? '50' : 150,
                        width: 300,
                    });
                };
                reader.readAsDataURL(file);
            } else {
                enqueueSnackbar(
                    `Only ${validExtension.join(', ').trimEnd()} ${validExtension?.length > 1 ? 'are' : 'is'} allowed.`,
                    {
                        action: (key) => <SnackbarDismiss key={key} />,
                        variant: 'error',
                    }
                );
            }
        };

        input.click();
    };

    const setup_function = (editor) => {
        editor.ui.registry.addButton('addTargetNodeLink', {
            icon: 'new-tab',
            tooltip: 'Add Target Node',
            onAction: async function () {
                handleTargetNodeAction(editor);
            },
        });
        editor.ui.registry.addButton('addPagebreak', {
            icon: 'pagebreakIcon',
            tooltip: 'Add Page Break',
            onAction: () =>
                editor.insertContent('<div class="pagebreak" style="page-break-before: always;"></div><p></p>'),
        });
    };

    const handleSubmit = useCallback(
        async (values, { setSubmitting }) => {
            dispatch(activateLoading({ showProgress: true }));

            var resultAction = null;

            let optButtons = values.option_buttons;
            optButtons = optButtons.map((btn, index) => {
                return {
                    ...btn,
                    button_id: index,
                    button_text: btn?.button_text?.trim(),
                };
            });

            let selectButtons;

            if (values.select_options) {
                selectButtons = values.select_options.map((btn, index) => {
                    return {
                        ...btn,
                        button_id: index,
                        option_text: btn?.option_text?.trim(),
                    };
                });

                values = {
                    ...values,
                    select_options: selectButtons,
                };
            }

            const contentIds = new Set();

            const addContentIds = (content) => {
                const doc = new DOMParser().parseFromString(content, 'text/html');

                const ids = Array.from(doc.querySelectorAll('[data-target-contentid]')).map(
                    (el) => +el.getAttribute('data-target-contentid')
                );

                ids.forEach((id) => contentIds.add(id));
            };

            if (values.content) {
                addContentIds(values.content);
            }

            if (Array.isArray(values.fields)) {
                for (const field of values.fields) {
                    if (field.labelcontent) {
                        addContentIds(field.labelcontent);
                    }

                    if (Array.isArray(field.fields)) {
                        for (const nestedField of field.fields) {
                            if (nestedField.labelcontent) {
                                addContentIds(nestedField.labelcontent);
                            }
                        }
                    }
                }
            }

            values = {
                ...values,
                option_buttons: optButtons,
                select_options: selectButtons,
                content_ids: Array.from(contentIds),
            };

            if (pendingFilesToUpload && pendingFilesToUpload.length > 0) {
                let currentContent = editorRef.current.getContent();

                for (let i = 0; i < pendingFilesToUpload.length; i++) {
                    const fl = pendingFilesToUpload[i];
                    resultAction = null;

                    if (currentContent.indexOf(fl.oldUrl) !== -1) {
                        const fileContentData = getFileExtension(fl.fileToUpload);

                        resultAction = await dispatch(
                            createContent({
                                asset_id: assetId,
                                display_name: fileContentData?.fileName,
                                content_type_id: fileContentData?.fileExtension,
                                content_version: {
                                    content_data: fl.fileToUpload,
                                    content_type_id: fileContentData?.fileExtension,
                                    content_version_type: 2,
                                },
                            })
                        );

                        if (!resultAction.error) {
                            currentContent = currentContent.replace(
                                fl.oldUrl,
                                resultAction?.payload?.last_content_version.url
                            );
                        }
                    }
                }

                values = {
                    ...values,
                    content: currentContent,
                };
            }

            if (values?.image_options && values.image_options.length > 0) {
                let currImgOpts = [...values.image_options];

                let currImgTargets = Array.isArray(values?.image_target_combinations)
                    ? [...values.image_target_combinations]
                    : [];

                for (let i = 0; i < values.image_options.length; i++) {
                    const fl = values.image_options[i];
                    resultAction = null;

                    if (fl?.img_data) {
                        const fileContentData = getFileExtension(fl.img_data);

                        resultAction = await dispatch(
                            createContent({
                                asset_id: assetId,
                                display_name: fileContentData?.fileName,
                                content_type_id: fileContentData?.fileExtension,
                                content_version: {
                                    content_data: fl.img_data,
                                    content_type_id: fileContentData?.fileExtension,
                                    content_version_type: 2,
                                },
                            })
                        );

                        if (!resultAction.error) {
                            currImgOpts[i] = {
                                ...fl,
                                img_data: null,
                                img_url: resultAction?.payload?.last_content_version.url,
                            };
                        }
                    }
                }

                currImgTargets = currImgTargets.map((targets) => {
                    return {
                        ...targets,
                        options_required: targets?.options_required?.map((opt) => {
                            return currImgOpts.find((img) => img.option_text === opt.option_text);
                        }),
                    };
                });

                values = {
                    ...values,
                    image_options: currImgOpts,
                    image_target_combinations: currImgTargets,
                };
            }

            dispatch(deactivateLoading());
            setSubmitting(false);
            onSubmit(values, null, nodes);
        },
        [dispatch, pendingFilesToUpload, onSubmit, nodes, assetId]
    );

    const validateForm = (values) => {
        let errors = {};
        if (!String(values?.title ?? '').trim()) {
            errors = {
                ...errors,
                title: 'Required.',
            };
        }
        if (values?.variable) {
            if (!isValidVariableName(values?.variable)) {
                errors = {
                    ...errors,
                    variable: 'Must start with letter followed by letters, numbers, or underscores',
                };
            } else if (!checkUniqueVariableName(values?.variable, `${values?.id}`)) {
                errors = {
                    ...errors,
                    variable: 'Must be unique in procedure',
                };
            }
        }
        switch (values?.answer_group) {
            case 'button':
                values?.option_buttons.map((buttonOption, index) => {
                    if (!buttonOption?.button_text?.trim()) {
                        errors = {
                            ...errors,
                            [`option_buttons[${index}].button_text`]: 'Required.',
                        };
                    }
                });
                break;
            case 'select':
                values?.select_options.map((selectOption, index) => {
                    if (!selectOption?.option_text?.trim()) {
                        errors = {
                            ...errors,
                            [`select_options[${index}].option_text`]: 'Required.',
                        };
                    }
                });
                break;
            default:
                break;
        }
        return errors;
    };

    // Called by the AssociatedView via setInitialView prop.
    // Opens the visual editor after loading the selected asset's orbit metadata.
    const handleOpenInitialViewDialog = async ({ asset_id, zone_id, item_id, initial_view } = {}) => {
        // prefill with existing node initial_view if present
        const existing = initial_view ?? node?.initial_view ?? {};
        setInitialViewValues({
            yaw: existing?.yaw ?? '',
            pitch: existing?.pitch ?? '',
            zoom: existing?.zoom ?? '',
        });

        // If a zone is provided, ensure it's loaded and visual edit is enabled before opening the editor.
        try {
            if (zone_id) {
                dispatch(activateLoading());
                dispatch(closeAllPanel());
                const targetAsset = asset_id ? await assetService.getById(asset_id, activeUser?.s3_prefix) : null;
                await dispatch(
                    loadZone({
                        zoneId: zone_id,
                    })
                ).unwrap();
                dispatch(
                    setOrbitImages({
                        orbit_images_url: targetAsset?.orbit_images_url,
                        orbit_images_count: targetAsset?.orbit_images_count,
                    })
                );
            }
        } catch (error) {
            console.error('Error preparing initial view editor:', error);
        } finally {
            if (zone_id) {
                dispatch(deactivateLoading());
            }
        }

        setGeminiTarget({ asset_id, zone_id, item_id, initial_view: existing });
        setShowGeminiEditor(true);
    };

    const handleSaveInitialView = () => {
        if (!node?.item_id) {
            setShowInitialViewDialog(false);
            return;
        }

        const parsed = {
            yaw: parseFloat(initialViewValues.yaw),
            pitch: parseFloat(initialViewValues.pitch),
            zoom: parseFloat(initialViewValues.zoom),
        };

        setFieldValueRef.current?.('initial_view', parsed);

        setShowInitialViewDialog(false);

        enqueueSnackbar('Saved initial view for this node (does not override asset default).', {
            action: (key) => <SnackbarDismiss key={key} />,
            variant: 'success',
        });
    };

    useEffect(() => {
        const handler = (ev) => {
            if (ev?.detail?.clear_initial_view === true) {
                if (setFieldValueRef.current) {
                    setFieldValueRef.current('initial_view', null);
                } else {
                    setInitialViewValues({ yaw: '', pitch: '', zoom: '' });
                }
                return;
            }

            const view = ev?.detail?.initial_view;
            if (!view) return;

            const parseValue = (value) => {
                if (value === undefined || value === null) return null;
                const number = typeof value === 'number' ? value : Number.parseFloat(value);
                return Number.isNaN(number) ? null : number;
            };

            const isOrbitView = view.init_view_flat_x !== undefined || view.init_view_flat_y !== undefined;
            const parsed = isOrbitView
                ? {
                      init_view_flat_x: parseValue(view.init_view_flat_x),
                      init_view_flat_y: parseValue(view.init_view_flat_y),
                      zoom: parseValue(view.zoom),
                  }
                : {
                      yaw: parseValue(view.yaw),
                      pitch: parseValue(view.pitch),
                      zoom: parseValue(view.zoom ?? view.fov),
                  };

            // write into Formik if available so submit includes initial_view
            if (setFieldValueRef.current) {
                setFieldValueRef.current('initial_view', parsed);
            } else {
                // fallback: keep in local state so dialog save still works
                setInitialViewValues(parsed);
            }
        };

        window.addEventListener('gemini:node-initial-view', handler);
        return () => window.removeEventListener('gemini:node-initial-view', handler);
    }, []);

    return (
        <div className={nodeFormStyles.container}>
            <div className={nodeFormStyles.form}>
                <Formik
                    key={`${node.id}-${node?.isNew ? 'new' : 'saved'}`}
                    enableReinitialize={true}
                    initialValues={formValues}
                    validate={validateForm}
                    onSubmit={handleSubmit}
                >
                    {({ values, isSubmitting, touched, setFieldTouched, dirty, resetForm, setFieldValue }) => {
                        // expose Formik setFieldValue so the window event listener can write initial_view into the form
                        setFieldValueRef.current = setFieldValue;

                        return (
                            <Form className={nodeFormStyles.formBody}>
                                <TitleFocusHandler />
                                <DirtyProcedure nodeId={node.id} isNewNode={isNewNode} />
                                <CommonNodeFormFields titleInputRef={titleInputRef} />
                                <NodeCategoryField />
                                <ErrorMessage name="title" component="div" />
                                <FormTextField label="Question" name="question" placeholder="Question" />
                                <ErrorMessage name="question" component="div" />
                                <FormTextField
                                    label="Assign answers to variable"
                                    name="variable"
                                    placeholder="Assign answers to variable"
                                />
                                <InputLabel sx={{ mx: 1, typography: 'body2' }}>Content</InputLabel>
                                <Field name="content">
                                    {({ field, form: { setFieldValue } }) => (
                                        <TinyMCEEditor
                                            id={editorInstanceId}
                                            tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
                                            onInit={(evt, editor) => (editorRef.current = editor)}
                                            plugins="addContent"
                                            init={{
                                                ...init,
                                                toolbar: `${init.toolbar} addTargetNodeLink addContentLink addInlineImage addInlineVideo addInlinePdf addInlineAudio addInlineLearningItem addPagebreak`,
                                                quickbars_insert_toolbar:
                                                    'table | addContentLink addInlineImage addInlineVideo',
                                                file_picker_types: 'media image',
                                                file_picker_callback: file_picker_callback_function,
                                                setup: setup_function,
                                            }}
                                            textareaName="content"
                                            onBlur={field.onBlur}
                                            onFocus={() => {
                                                if (!touched?.content) {
                                                    setFieldTouched('content', true);
                                                }
                                            }}
                                            onEditorChange={(content) => {
                                                if (
                                                    touched?.content ||
                                                    editorRef.current?.isInsertingSnippet ||
                                                    editorRef.current?.isInsertingContent
                                                ) {
                                                    setFieldValue(field.name, content);
                                                    if (editorRef.current?.isInsertingSnippet) {
                                                        editorRef.current.isInsertingSnippet = false;
                                                    }
                                                    if (editorRef.current?.isInsertingContent) {
                                                        editorRef.current.isInsertingContent = false;
                                                    }
                                                }
                                            }}
                                            value={field.value}
                                        />
                                    )}
                                </Field>
                                <AssociatedView
                                    asset_id={values?.asset_id}
                                    zone_id={values?.zone_id}
                                    item_id={values?.item_id}
                                    node={node}
                                    initial_view={values?.initial_view}
                                    close_associated_view={values?.close_associated_view}
                                    setInitialView={handleOpenInitialViewDialog}
                                />
                                <FormControl fullWidth>
                                    <Box
                                        display={showTwoPanelMode === true ? 'block' : 'flex'}
                                        flexWrap="wrap"
                                        width="100%"
                                    >
                                        <ContentNodeFormFields
                                            nodes={nodes}
                                            openEditFieldForm={openEditFieldForm}
                                            setOpenEditFieldForm={setOpenEditFieldForm}
                                            selectedField={selectedField}
                                            isValidVariableName={isValidVariableName}
                                            checkUniqueVariableName={checkUniqueVariableName}
                                        />
                                        <ContentNodeFormPreview
                                            procedureRef={procedureRef}
                                            selectedField={selectedField}
                                            setSelectedField={setSelectedField}
                                        />
                                    </Box>
                                </FormControl>
                                <ContentNodeFormFailureDetails />
                                <ContentNodeFormSettings
                                    selectCompletionNode={selectCompletionNode}
                                    completionNode={completionNode}
                                />
                                <ContentNodeFormPreset />
                                <Box
                                    component="fieldset"
                                    border="1px solid #cccccc"
                                    borderRadius="5px"
                                    padding="9px !important"
                                    sx={{ containerType: 'inline-size' }}
                                >
                                    <ContentNodeFormAnswerOptions />
                                    <ContentNodeFormAnswerOptionFields node={node} nodes={nodes} />
                                </Box>
                                {openSaveDataDialog && (
                                    <SaveNodeDataDialog
                                        openSaveDataDialog={openSaveDataDialog}
                                        setOpenSaveDataDialog={setOpenSaveDataDialog}
                                        isSubmitting={isSubmitting}
                                        onFocusTitleField={focusTitleField}
                                        onCancel={() => {
                                            if (!node?.isNew) {
                                                resetForm();
                                            }
                                            onCancel();
                                        }}
                                    />
                                )}
                                <div className={nodeFormStyles.submitButtonRow}>
                                    <button
                                        className={commonFormStyles.cancel}
                                        type="button"
                                        // disabled={isSubmitting || (!dirty && showTwoPanelMode)}
                                        onClick={() => {
                                            if (dirty) {
                                                setOpenSaveDataDialog(true);
                                            } else {
                                                if (!node?.isNew) {
                                                    resetForm();
                                                }
                                                onCancel();
                                            }
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button className={commonFormStyles.submit} type="submit" disabled={isSubmitting}>
                                        Submit
                                    </button>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
                <Dialog
                    fullScreen={true}
                    maxWidth="xl"
                    open={showGeminiEditor}
                    onClose={() => setShowGeminiEditor(false)}
                >
                    <GeminiEditor
                        onClose={() => setShowGeminiEditor(false)}
                        initialViewOnly={true}
                        closeOnSetInitialView={true}
                        initialHighlightTarget={geminiTarget}
                    />
                </Dialog>
                {/*
                   Small dialog to accept yaw/pitch/zoom for the node. Authors can manually paste or type values
                   (or use a preview that emits viewer values that you can paste here). Saving sets `initial_view`
                   on the node form values. The viewer must listen to 'gemini:set-node-initial-view' to apply
                   these values at runtime when the node is activated.
                 */}
                <Dialog open={showInitialViewDialog} onClose={() => setShowInitialViewDialog(false)}>
                    <DialogTitle>Set Initial View for Node</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Yaw"
                            fullWidth
                            value={initialViewValues.yaw}
                            onChange={(e) => setInitialViewValues((s) => ({ ...s, yaw: e.target.value }))}
                            margin="dense"
                        />
                        <TextField
                            label="Pitch"
                            fullWidth
                            value={initialViewValues.pitch}
                            onChange={(e) => setInitialViewValues((s) => ({ ...s, pitch: e.target.value }))}
                            margin="dense"
                        />
                        <TextField
                            label="Zoom"
                            fullWidth
                            value={initialViewValues.zoom}
                            onChange={(e) => setInitialViewValues((s) => ({ ...s, zoom: e.target.value }))}
                            margin="dense"
                        />
                        <Box mt={1} fontSize="0.85rem" color="text.secondary">
                            Note: this sets the initial view for this node only. It does not change the asset default
                            initial view.
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowInitialViewDialog(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleSaveInitialView}>
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
                {selectTargetNodeForEditor && (
                    <SelectTargetNodeForm
                        nodes={nodes}
                        activeNode={node}
                        loadedTargetNode={loadedTargetNode}
                        onSelectedTargetNode={(values) => {
                            handleSelectedTargetNode(values, selectTargetNodeForEditor);
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default ContentNodeForm;
